import { StorageAdapter } from '../storage/StorageAdapter'
import { MetaRepository } from '../repositories/MetaRepository'
import { recordBreadcrumb, captureOfflineError } from '../telemetry'

/**
 * Cache offline de los CATÁLOGOS del diálogo "Agregar tarea" (Llenar Formulario).
 *
 * Sin red (o con red intermitente), las 6 lecturas del diálogo fallaban con Network Error y los
 * catch dejaban los selects VACÍOS ("No hay áreas configuradas", Área en blanco) aunque la capa
 * offline ya tuviera datos en SQLite. Este módulo es la capa PURA de cache (patrón ISS-843 /
 * signature-roles.js): solo SQLite (meta + c_hierarchies + c_areas), CERO transporte, todo
 * best-effort — las lecturas devuelven `null` cuando no hay cache y NUNCA lanzan. El branch
 * online/offline vive en el store (stores/tasks/actions.js::readThroughCatalog), no aquí.
 *
 * DOS FUENTES, deliberadamente distintas:
 *   - `meta` (KV): los reads sin tabla c_* propia — config de org (ap_categories/ap_categories2),
 *     categorías-por-sitio y áreas-por-categoría. Se cachean READ-THROUGH (cada fetch online
 *     exitoso persiste el body CRUDO tal cual, para devolver el shape EXACTO que el consumidor
 *     ya parsea). Schema v1 congelado → meta es la vía sin migración.
 *   - `c_hierarchies` / `c_areas`: las mantiene el pull Tier B del sync-worker (TTL 12h); aquí
 *     SOLO se leen (JSON.parse de la columna `data` = fila cruda del backend). No hay cachePut
 *     para ellas: el dueño del ciclo de vida es el pull engine.
 *
 * PREFIJOS DE CLAVE en meta (documentados también en MetaRepository.js):
 *   - `apcat:<orgId>:<type>`         body de GET organizations/<id>/config/?type=<type>
 *   - `hiersitecat:<siteId>`         body de GET task-hierarchy/sites/<id>/categories
 *                                    (siteId es id global del backend: no colisiona entre orgs)
 *   - `areas:<orgId>:<category>`     array de GET fetchAreas?category=<category> (org-scoped:
 *                                    en device compartido, las áreas de la org A no deben
 *                                    servirse a un usuario de la org B)
 *   - `warm:apcat:<orgId>`           epoch ms del último warm-up (sync/pull/org-config.js)
 */

export const orgConfigKey = (orgId, type) => `apcat:${orgId}:${type}`
export const siteCategoriesKey = siteId => `hiersitecat:${siteId}`
export const filteredAreasKey = (orgId, category) => `areas:${orgId}:${category}`
export const warmOrgConfigKey = orgId => `warm:apcat:${orgId}`

/** Parse defensivo: null si el valor no existe o no es JSON válido. */
const parseOrNull = raw => {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Cachea el body CRUDO de GET organizations/<id>/config/?type=<type>. Se exige un objeto CON la
 * clave `data` — eso filtra la basura del interceptor (un AxiosError resuelto no la trae) pero SÍ
 * acepta `{data:null}`, que es la respuesta legítima del backend para una org sin ese type
 * configurado (fetchTags2 ya la maneja como lista vacía). Best-effort: nunca lanza.
 */
export async function cacheOrgConfig (orgId, type, body) {
  if (!orgId || !type || !body || typeof body !== 'object' || !('data' in body)) return
  try {
    await MetaRepository.put(orgConfigKey(orgId, type), JSON.stringify(body))
    recordBreadcrumb('offline.catalogs', `org-config ${type} cacheado (org ${orgId})`)
  } catch (error) {
    captureOfflineError(error, { phase: 'cacheOrgConfig', type })
  }
}

/** Body cacheado de org-config, o null. Mismo shape que resuelve getGeneralSettings. */
export async function getCachedOrgConfig (orgId, type) {
  if (!orgId || !type) return null
  try {
    return parseOrNull(await MetaRepository.get(orgConfigKey(orgId, type)))
  } catch (error) {
    captureOfflineError(error, { phase: 'getCachedOrgConfig', type })
    return null
  }
}

/**
 * Cachea el body de GET task-hierarchy/sites/<id>/categories ({data:[...]}). Solo con
 * body.data array (no pisa con vacío no-array). Best-effort.
 */
export async function cacheSiteCategories (siteId, body) {
  if (siteId == null || !body || !Array.isArray(body.data)) return
  try {
    await MetaRepository.put(siteCategoriesKey(siteId), JSON.stringify(body))
  } catch (error) {
    captureOfflineError(error, { phase: 'cacheSiteCategories' })
  }
}

/** Body cacheado ({data:[...]}) de categorías del sitio, o null. */
export async function getCachedSiteCategories (siteId) {
  if (siteId == null) return null
  try {
    return parseOrNull(await MetaRepository.get(siteCategoriesKey(siteId)))
  } catch (error) {
    captureOfflineError(error, { phase: 'getCachedSiteCategories' })
    return null
  }
}

/** Cachea la lista (string[]) de GET fetchAreas?category=<category>. Solo arrays (incluye []:
 * "esta categoría no tiene áreas" es un resultado legítimo del server). Best-effort. */
export async function cacheFilteredAreas (orgId, category, list) {
  if (!orgId || !category || !Array.isArray(list)) return
  try {
    await MetaRepository.put(filteredAreasKey(orgId, category), JSON.stringify(list))
  } catch (error) {
    captureOfflineError(error, { phase: 'cacheFilteredAreas' })
  }
}

/**
 * Áreas para una categoría, en dos niveles:
 *   1) meta `areas:<orgId>:<category>` — el resultado EXACTO cacheado la última vez que ese
 *      filtro corrió online. Se respeta TAL CUAL, incluso `[]`: si el server dijo que esa
 *      categoría no tiene áreas, offline debe mostrar lo mismo (NO caer al fallback global,
 *      que ofrecería áreas de otras categorías y produciría tasks con área inválida).
 *   2) fallback SOLO sin key exacta: la lista GLOBAL de c_areas (la puebla revalidateStale SIN
 *      categoría — catalogs.js:226 — no filtrable localmente; todas-las-áreas > select vacío).
 * @returns {Promise<string[]|null>} null si ninguna fuente tiene datos.
 */
export async function getCachedFilteredAreas (orgId, category) {
  try {
    if (orgId && category) {
      const exact = parseOrNull(await MetaRepository.get(filteredAreasKey(orgId, category)))
      if (Array.isArray(exact)) return exact
    }
    const res = await StorageAdapter.query('SELECT data FROM c_areas', [])
    const rows = (res && res.values) || []
    if (!rows.length) return null
    return rows.map(r => parseOrNull(r.data)).filter(v => v != null)
  } catch (error) {
    captureOfflineError(error, { phase: 'getCachedFilteredAreas' })
    return null
  }
}

/**
 * Lee de c_hierarchies las filas de un kind y devuelve el MISMO shape que el endpoint online
 * ({data:[filas crudas]}), o null si el catálogo aún no se pulleó (0 filas ≠ catálogo vacío
 * confiable: puede ser cold-start; null deja al caller decidir el degradado).
 */
async function readHierarchyKind (kind, phase) {
  try {
    const res = await StorageAdapter.query('SELECT data FROM c_hierarchies WHERE kind = ?', [kind])
    const rows = (res && res.values) || []
    if (!rows.length) return null
    return { data: rows.map(r => parseOrNull(r.data)).filter(v => v != null) }
  } catch (error) {
    captureOfflineError(error, { phase })
    return null
  }
}

/** Sitios de jerarquía cacheados ({data:[{id,name,area_id,...}]}), o null. */
export async function getCachedSites () {
  return readHierarchyKind('site', 'getCachedSites')
}

/** Subcategorías de jerarquía cacheadas ({data:[{id,name,category_id,default_priority,...}]}), o null. */
export async function getCachedSubcategories () {
  return readHierarchyKind('subcategory', 'getCachedSubcategories')
}

// Tablas de catálogo (lectura tenant-scoped que el pull Tier B repuebla). Se vacían al cambiar de usuario.
const CATALOG_TABLES = [
  'c_areas', 'c_hierarchies', 'c_catalog_elements', 'c_locations',
  'c_users', 'c_forms', 'c_form_groups', 'c_form_items', 'c_tags', 'c_roles'
]

/**
 * WIPE de catálogos al CAMBIAR DE USUARIO [review Hugo #3]. `c_areas`/`c_hierarchies` (y las demás c_*)
 * NO tienen columna de organización y el pull Tier B hace full-replace con TTL de 12h; en un device
 * COMPARTIDO entre turnos, el usuario B (org B) podía leer sitios/áreas cacheados por A (org A) via el
 * fallback global `SELECT ... FROM c_areas` (getCachedFilteredAreas) o `getCachedSites()`, y crear una
 * tarea con un site_id que no le corresponde = exposición de datos entre organizaciones.
 *
 * Fix B (invalidar al cambiar de usuario): al hacer logout se borran los catálogos + sus marcas de
 * frescura. `cursor:%` cubre TODOS los cursores de pull en meta (`cursor:<catalog>` de Tier B, y
 * cualquier cursor futuro bajo el mismo prefijo); borrarlos en el cambio de usuario es INTENCIONAL e
 * inocuo (no toca datos, solo fuerza re-pull en el próximo tick en vez de saltarlo por el TTL).
 * Junto con `apcat:*`/`areas:*`/`warm:*`/`hiersitecat:*` (cache de org),
 * el SIGUIENTE usuario re-pullea LO SUYO (sin esto el TTL de 12h consideraba el catálogo "fresco").
 * NO toca los DATOS del usuario (occurrences/answers/addons/outbox: ya user_id-scoped).
 * Best-effort: se llama desde el logout; un fallo aquí no rompe el logout.
 * @param {{ db?: object }} deps
 */
export async function wipeCatalogsForUserChange ({ db = StorageAdapter } = {}) {
  for (const t of CATALOG_TABLES) {
    try { await db.run(`DELETE FROM ${t}`) } catch { /* tabla ausente en un schema viejo: noop */ }
  }
  try {
    await db.run(
      "DELETE FROM meta WHERE key LIKE 'cursor:%' OR key LIKE 'apcat:%' OR key LIKE 'areas:%' " +
      "OR key LIKE 'warm:%' OR key LIKE 'hiersitecat:%'"
    )
  } catch { /* noop: el resto de meta (idempotencia/migración) se conserva */ }
}
